import { z } from 'zod';
import {
  SPO_DEFAULT,
  SPO_DEFAULT_INTL,
  SPO_ITEM_STOCK__MAX,
  SPO_ITEMS__MAX,
  SPO_OPTION_VALUES__MAX,
  SPO_OPTION_VALUES__MIN,
  SPO_OPTIONS__MAX,
  SPO_TEXT_OPTION__MAX,
  StockType,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import { commify } from '@services/MarppleShop/shared/util/commify.ts';

const spoOptionValueSchema = z.object({
  name: z.string().min(1, { message: '옵션 상세 정보를 다시 확인해 주세요.' }),
});

export const spoOptionSchema = z.object({
  name: z.string().min(1, { message: '옵션명은 필수 사항입니다.' }),
  _: z.object({
    spo_option_values: z
      .array(spoOptionValueSchema)
      .min(SPO_OPTION_VALUES__MIN, {
        message: `옵션 상세 정보는 ${commify(SPO_OPTION_VALUES__MIN)}개 이상 필요합니다.`,
      })
      .max(SPO_OPTION_VALUES__MAX, {
        message: `옵션 상세 정보는 ${commify(SPO_OPTION_VALUES__MAX)}개 이하까지 가능합니다.`,
      })
      .refine(
        (values) => {
          const names = values.map((v) => v.name);
          return new Set(names).size === names.length;
        },
        { message: '옵션값은 중복이 불가합니다.' },
      ),
  }),
});

export const spoTextOptionSchema = z.object({
  id: z.string().optional(),
  name: z.string().max(SPO_TEXT_OPTION__MAX).optional(),
});

export const groupNamesOptionNamesSchema = z.object({
  group_name: z.string(),
  option_name: z.string(),
});

const spoOptionValueIntlSchema = spoOptionValueSchema.extend({
  name_en: z.string().min(1, { message: '옵션 상세 정보를 다시 확인해 주세요.' }),
  name_jp: z.string().min(1, { message: '옵션 상세 정보를 다시 확인해 주세요.' }),
});

const spoOptionIntlSchema = spoOptionSchema.extend({
  name_en: z.string().min(1, { message: '옵션명은 필수 사항입니다.' }),
  name_jp: z.string().min(1, { message: '옵션명은 필수 사항입니다.' }),
  _: z.object({
    spo_option_values: z
      .array(spoOptionValueIntlSchema)
      .min(SPO_OPTION_VALUES__MIN, {
        message: `옵션 상세 정보는 ${commify(SPO_OPTION_VALUES__MIN)}개 이상 필요합니다.`,
      })
      .max(SPO_OPTION_VALUES__MAX, {
        message: `옵션 상세 정보는 ${commify(SPO_OPTION_VALUES__MAX)}개 이하까지 가능합니다.`,
      })
      .refine(
        (values) => {
          const names = values.map((v) => v.name);
          return new Set(names).size === names.length;
        },
        { message: '옵션값은 중복이 불가합니다.' },
      ),
  }),
});

const spoTextOptionIntlSchema = spoTextOptionSchema.extend({
  name_en: z.string(),
  name_jp: z.string(),
});

export const groupNamesOptionNamesIntlSchema = groupNamesOptionNamesSchema.extend({
  group_name_en: z.string(),
  group_name_jp: z.string(),
  option_name_en: z.string(),
  option_name_jp: z.string(),
});

const spoItemBaseSchema = z.object({
  price: z.number().int().min(0).default(0),
  sku: z
    .object({
      nickname: z.string().max(100),
    })
    .optional(),
  _: z.object({
    spo_item_stock: z.object({
      quantity: z
        .number()
        .int()
        .min(0)
        .max(SPO_ITEM_STOCK__MAX, { message: '수량은 최대 10억개 미만으로 설정해야 합니다.' }),
    }),
  }),
  sold_quantity: z.number().int().min(0).default(0),
});

export const spoItemSchema = spoItemBaseSchema.extend({
  group_names_option_names: z.array(groupNamesOptionNamesSchema).default([]),
});

export const spoItemIntlSchema = spoItemBaseSchema.extend({
  group_names_option_names: z.array(groupNamesOptionNamesIntlSchema).default([]),
});

export const spoSchema = z
  .object({
    stock_type: z.nativeEnum(StockType).default(SPO_DEFAULT.stock_type),
    _: z.object({
      spo_options: z
        .array(spoOptionSchema)
        .max(SPO_OPTIONS__MAX, { message: `옵션은 ${commify(SPO_OPTIONS__MAX)}개까지 입력 가능합니다.` })
        .refine((options) => new Set(options.map((opt) => opt.name)).size === options.length, {
          message: '중복된 옵션명이 존재합니다.',
        })
        .default(SPO_DEFAULT._.spo_options),
      spo_items: z
        .array(spoItemSchema)
        .max(SPO_ITEMS__MAX, { message: `옵션은 최대 ${commify(SPO_ITEMS__MAX)}개 까지 생성할 수 있습니다.` })
        .default(SPO_DEFAULT._.spo_items),
      spo_text_options: z.array(spoTextOptionSchema).default(SPO_DEFAULT._.spo_text_options),
      spo_producing_period: z
        .object({
          spo_id: z.number().optional(),
          max_days: z.number().int().min(1).nullable(),
        })
        .optional(),
    }),
  })
  .default(SPO_DEFAULT);

export const spoIntlSchema = z
  .object({
    stock_type: z.nativeEnum(StockType).default(SPO_DEFAULT_INTL.stock_type),
    _: z.object({
      spo_options: z
        .array(spoOptionIntlSchema)
        .max(SPO_OPTIONS__MAX, { message: `옵션은 ${commify(SPO_OPTIONS__MAX)}개까지 입력 가능합니다.` })
        .refine(
          (options) => {
            const names = options.map((opt) => opt.name);
            return new Set(names).size === names.length;
          },
          { message: '중복된 옵션명이 존재합니다.' },
        )
        .default(SPO_DEFAULT_INTL._.spo_options),
      spo_items: z
        .array(spoItemIntlSchema)
        .max(SPO_ITEMS__MAX, { message: `옵션은 최대 ${commify(SPO_ITEMS__MAX)}개 까지 생성할 수 있습니다.` })
        .default(SPO_DEFAULT_INTL._.spo_items),
      spo_text_options: z.array(spoTextOptionIntlSchema).default(SPO_DEFAULT_INTL._.spo_text_options),
      spo_producing_period: z
        .object({
          spo_id: z.number().optional(),
          max_days: z.number().int().min(1),
        })
        .optional(),
    }),
  })
  .default(SPO_DEFAULT_INTL);
